/**
 * Initialises the client-side stores that every layout depends on (basket + wishlist).
 *
 * Call it once from a layout's `<script setup>`; await it there only where the page reads these stores
 * as it mounts, since a top-level await holds hydration. Add any new shared store initialisation to
 * the `Promise.all` array below — do NOT add it as a separate top-level `await` statement in the layout.
 *
 * IMPORTANT: Vue 3.5's `<script setup>` compiler wraps each top-level await in a parenthesised
 * `withAsyncContext(...)` expression and, in our no-semicolon style, emits no separator between them.
 * Two adjacent top-level awaits therefore compile to `(...)(...)`, where the first is invoked as a
 * function and throws `__restore is not a function` at runtime (~1-2s after load, when the promise
 * resolves). Funnelling every shared initialisation through this single awaited call keeps each
 * layout to one top-level await and avoids that footgun.
 */
export function initLayoutStores(): Promise<void> {
  // A layout switch can arrive mid-load; a second concurrent load would create a second wishlist.
  if (layoutStoresLoadInFlight) return layoutStoresLoadInFlight
  const basketsStore = useBasketsStore()
  const wishlistStore = useWishlistStore()
  const status = useLayoutStoresStatus()

  // Each settles on its own, so a cart rule needn't wait on the wishlist's product fetch.
  const settle = async (store: keyof LayoutStoresStatus, init: () => Promise<void>, failed: () => boolean) => {
    // A reload clears the store before refilling it, so rules must not read it as still ready.
    status.value[store] = 'pending'
    try {
      await init()
      // `failed` and `ready` are not interchangeable: a cart we could not read looks empty, so a
      // consumer that would misjudge it needs to be able to tell which one it got.
      status.value[store] = failed() ? 'failed' : 'ready'
    } catch (error) {
      status.value[store] = 'failed'
      throw error
    }
  }

  // Both loads skip silently without a customer id, so they must start after the user is resolved.
  const ready = pendingUserResolution
    .then(() =>
      Promise.all([
        settle('cart', basketsStore.init, () => basketsStore.initFailed),
        settle('wishlist', wishlistStore.init, () => wishlistStore.initFailed),
      ]),
    )
    .then(() => undefined)
    .finally(() => {
      layoutStoresLoadInFlight = undefined
    })
  latestLayoutStoresLoad = ready
  layoutStoresLoadInFlight = ready
  return ready
}

// Client-only: layouts call initLayoutStores from `import.meta.client` branches.
let latestLayoutStoresLoad: Promise<void> | undefined
let layoutStoresLoadInFlight: Promise<void> | undefined
let pendingUserResolution: Promise<unknown> = Promise.resolve()

/** Holds the next layout store load until a user resolution running alongside hydration settles. */
export function loadLayoutStoresAfter(userResolution: Promise<unknown>): void {
  pendingUserResolution = userResolution.catch(() => undefined)
}

/**
 * Resolves once the latest basket + wishlist load has settled (never rejects). Browse layouts render
 * without waiting for it, so anything that reads or writes those stores early must wait here first.
 */
export function whenLayoutStoresSettled(): Promise<void> {
  return latestLayoutStoresLoad ? latestLayoutStoresLoad.catch(() => undefined) : Promise.resolve()
}

export type LayoutStoreStatus = 'pending' | 'ready' | 'failed'

export type LayoutStoresStatus = { cart: LayoutStoreStatus; wishlist: LayoutStoreStatus }

/**
 * How each layout store's first load went — read by consumers that can arrive before it finishes,
 * e.g. Braze campaign rules, which suppress a cart or wishlist rule they cannot honestly evaluate.
 */
export function useLayoutStoresStatus() {
  return useState<LayoutStoresStatus>('layout-stores-status', () => ({ cart: 'pending', wishlist: 'pending' }))
}
